/**
 * SIM Card ICCID Generator
 * ========================
 *
 * Generates realistic SIM card ICCIDs (Integrated Circuit Card Identifiers)
 * following international standards for IoT/Telecom industry.
 *
 * ICCID Format: 19-20 digits
 * Structure: 89 (Telecom) + CC (Country) + MNC + Account Number + Checksum
 */

/**
 * Generate Luhn checksum digit for ICCID validation
 */
function generateLuhnChecksum(digits: string): number {
  let sum = 0
  let alternate = false

  // Process from right to left
  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = parseInt(digits[i], 10)

    if (alternate) {
      digit *= 2
      if (digit > 9) {
        digit -= 9
      }
    }

    sum += digit
    alternate = !alternate
  }

  return (10 - (sum % 10)) % 10
}

/**
 * Mobile Network Operators (MNCs) by country
 */
const MOBILE_NETWORKS = {
  // United States (Country Code: 1)
  US: [
    { name: 'AT&T', mnc: '10', prefix: '8910' },
    { name: 'Verizon', mnc: '11', prefix: '8911' },
    { name: 'T-Mobile', mnc: '12', prefix: '8912' },
    { name: 'Sprint', mnc: '13', prefix: '8913' },
  ],
  // United Kingdom (Country Code: 44)
  UK: [
    { name: 'Vodafone UK', mnc: '20', prefix: '894420' },
    { name: 'EE', mnc: '30', prefix: '894430' },
    { name: 'Three UK', mnc: '50', prefix: '894450' },
  ],
  // India (Country Code: 91)
  IN: [
    { name: 'Airtel', mnc: '10', prefix: '899110' },
    { name: 'Jio', mnc: '20', prefix: '899120' },
    { name: 'VI (Vodafone Idea)', mnc: '30', prefix: '899130' },
  ],
  // Germany (Country Code: 49)
  DE: [
    { name: 'Deutsche Telekom', mnc: '01', prefix: '894901' },
    { name: 'Vodafone DE', mnc: '02', prefix: '894902' },
    { name: 'O2 Germany', mnc: '03', prefix: '894903' },
  ],
  // China (Country Code: 86)
  CN: [
    { name: 'China Mobile', mnc: '00', prefix: '898600' },
    { name: 'China Unicom', mnc: '01', prefix: '898601' },
    { name: 'China Telecom', mnc: '11', prefix: '898611' },
  ],
  // Global IoT (Multi-country)
  GLOBAL: [
    { name: 'Twilio IoT', mnc: '99', prefix: '899999' },
    { name: 'Hologram', mnc: '88', prefix: '899988' },
    { name: 'Particle', mnc: '77', prefix: '899977' },
  ]
}

/**
 * SIM Card Status Types
 */
export enum SIMStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  DEACTIVATED = 'deactivated',
  PENDING_ACTIVATION = 'pending_activation',
  EXPIRED = 'expired'
}

/**
 * SIM Card interface
 */
export interface SIMCard {
  iccid: string
  displayName: string
  carrier: string
  country: string
  status: SIMStatus
  activationDate: string
  lastActivity: string
  dataUsageMB: number
  monthlyLimitMB: number
}

/**
 * Generate a realistic ICCID
 *
 * @param country - Country code (US, UK, IN, DE, CN, GLOBAL)
 * @param carrierIndex - Index of carrier in country's network list (optional, random if not provided)
 * @param accountSeed - Seed for generating account number (optional, uses random if not provided)
 * @returns Complete ICCID string (19-20 digits)
 */
export function generateICCID(
  country: keyof typeof MOBILE_NETWORKS = 'US',
  carrierIndex?: number,
  accountSeed?: number
): string {
  const networks = MOBILE_NETWORKS[country]
  const networkIndex = carrierIndex !== undefined
    ? carrierIndex % networks.length
    : Math.floor(Math.random() * networks.length)

  const network = networks[networkIndex]

  // Generate 12-digit account number
  const seed = accountSeed !== undefined ? accountSeed : Math.floor(Math.random() * 1000000)
  const accountNumber = String(seed).padStart(12, '0').slice(0, 12)

  // Combine prefix + account number
  const baseDigits = network.prefix + accountNumber

  // Add Luhn checksum
  const checksum = generateLuhnChecksum(baseDigits)

  return baseDigits + checksum
}

/**
 * Generate a SIM card object with realistic data
 *
 * @param index - Index for deterministic generation
 * @param country - Country code
 * @returns Complete SIM card object
 */
export function generateSIMCard(
  index: number,
  country: keyof typeof MOBILE_NETWORKS = 'US'
): SIMCard {
  const networks = MOBILE_NETWORKS[country]
  const carrierIndex = index % networks.length
  const network = networks[carrierIndex]

  const iccid = generateICCID(country, carrierIndex, 1000000 + index)

  // Generate realistic status distribution
  const statusRand = Math.random()
  let status: SIMStatus
  if (statusRand < 0.80) status = SIMStatus.ACTIVE
  else if (statusRand < 0.90) status = SIMStatus.INACTIVE
  else if (statusRand < 0.95) status = SIMStatus.SUSPENDED
  else status = SIMStatus.PENDING_ACTIVATION

  // Generate activation date (last 1-3 years)
  const daysAgo = Math.floor(Math.random() * 1095) // 3 years
  const activationDate = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000)

  // Last activity (0-30 days ago for active, longer for inactive)
  const lastActivityDays = status === SIMStatus.ACTIVE
    ? Math.floor(Math.random() * 30)
    : Math.floor(Math.random() * 180) + 30
  const lastActivity = new Date(Date.now() - lastActivityDays * 24 * 60 * 60 * 1000)

  // Data usage (realistic for IoT)
  const dataUsageMB = status === SIMStatus.ACTIVE
    ? Math.floor(Math.random() * 500) + 50  // 50-550 MB
    : Math.floor(Math.random() * 100)  // 0-100 MB

  const monthlyLimitMB = 1000  // Standard IoT plan

  return {
    iccid,
    displayName: `SIM ${iccid.slice(-6)}`,  // Last 6 digits for display
    carrier: network.name,
    country,
    status,
    activationDate: activationDate.toISOString().split('T')[0],
    lastActivity: lastActivity.toISOString(),
    dataUsageMB,
    monthlyLimitMB
  }
}

/**
 * Generate multiple SIM cards
 *
 * @param count - Number of SIM cards to generate
 * @param country - Country code
 * @returns Array of SIM card objects
 */
export function generateSIMCards(
  count: number,
  country: keyof typeof MOBILE_NETWORKS = 'US'
): SIMCard[] {
  return Array.from({ length: count }, (_, i) => generateSIMCard(i, country))
}

/**
 * Parse ICCID to extract components
 *
 * @param iccid - ICCID string
 * @returns Parsed components
 */
export function parseICCID(iccid: string): {
  industry: string
  countryCode: string
  mnc: string
  accountNumber: string
  checksum: string
  isValid: boolean
} {
  if (iccid.length < 19 || iccid.length > 20) {
    return {
      industry: '',
      countryCode: '',
      mnc: '',
      accountNumber: '',
      checksum: '',
      isValid: false
    }
  }

  const industry = iccid.slice(0, 2)  // Should be '89'
  const countryCode = iccid.slice(2, 4)
  const mnc = iccid.slice(4, 6)
  const accountNumber = iccid.slice(6, -1)
  const checksum = iccid.slice(-1)

  // Validate Luhn checksum
  const calculatedChecksum = generateLuhnChecksum(iccid.slice(0, -1))
  const isValid = industry === '89' && checksum === String(calculatedChecksum)

  return {
    industry,
    countryCode,
    mnc,
    accountNumber,
    checksum,
    isValid
  }
}

/**
 * Format ICCID for display (with spacing for readability)
 *
 * @param iccid - ICCID string
 * @returns Formatted ICCID (e.g., "8991 1012 0000 3204 510")
 */
export function formatICCID(iccid: string): string {
  return iccid.replace(/(\d{4})(?=\d)/g, '$1 ')
}

/**
 * Map old asset IDs to SIM ICCIDs
 * This provides a deterministic mapping from legacy F-codes to ICCIDs
 */
const ASSET_ID_TO_ICCID_MAP = new Map<string, string>()

/**
 * Get or generate ICCID for a legacy asset ID
 *
 * @param assetId - Legacy asset ID (e.g., "F67548")
 * @param country - Country code for new ICCIDs
 * @returns ICCID string
 */
export function getICCIDForAssetId(
  assetId: string,
  country: keyof typeof MOBILE_NETWORKS = 'US'
): string {
  // Check if we already have a mapping
  if (ASSET_ID_TO_ICCID_MAP.has(assetId)) {
    return ASSET_ID_TO_ICCID_MAP.get(assetId)!
  }

  // Generate deterministic ICCID from asset ID
  // Extract numeric part from asset ID (e.g., "F67548" -> 67548)
  const numericPart = parseInt(assetId.replace(/[^\d]/g, ''), 10) || 0

  const iccid = generateICCID(country, undefined, numericPart)
  ASSET_ID_TO_ICCID_MAP.set(assetId, iccid)

  return iccid
}

/**
 * Generate display name for SIM card in asset intelligence context
 *
 * @param assetId - Asset ID or ICCID
 * @param country - Country code
 * @returns User-friendly display name
 */
export function generateSIMDisplayName(
  assetId: string,
  country: keyof typeof MOBILE_NETWORKS = 'US'
): string {
  // If it's already an ICCID (19-20 digits), format it
  if (/^\d{19,20}$/.test(assetId)) {
    return `SIM ${assetId.slice(-6)}`  // Use last 6 digits
  }

  // Otherwise, generate ICCID and use last 6 digits
  const iccid = getICCIDForAssetId(assetId, country)
  return `SIM ${iccid.slice(-6)}`
}
